import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Form, FormControl, FormField, FormItem, FormLabel, FormDescription, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Save, Globe, Facebook, Youtube, Instagram } from 'lucide-react';
import AvatarUpload from './AvatarUpload';
import { validateSocialMediaUrl } from '@/utils/socialMediaValidation';

interface SocialLinks {
  tiktok?: string;
  facebook?: string;
  website?: string;
  youtube?: string;
  instagram?: string;
}

interface UserProfile {
  id: string;
  user_id: string;
  username: string;
  display_name: string;
  bio?: string;
  avatar_url?: string;
  is_public: boolean;
  social_links?: SocialLinks;
  created_at: string;
  updated_at: string;
}

export interface ProfileFormValues {
  fullName: string;
  bio: string;
  username: string;
  socialLinks: SocialLinks;
}

interface UserProfileFormProps {
  user: UserProfile;
  onSave: (values: ProfileFormValues) => void;
  onCancel: () => void;
}

const UserProfileForm: React.FC<UserProfileFormProps> = ({
  user,
  onSave,
  onCancel
}) => {
  const form = useForm<ProfileFormValues>({
    defaultValues: {
      fullName: user?.display_name || user?.full_name || '',
      bio: user?.bio || '',
      username: user?.username || '',
      socialLinks: {
        tiktok: user?.social_links?.tiktok || '',
        facebook: user?.social_links?.facebook || '',
        website: user?.social_links?.website || '',
        youtube: user?.social_links?.youtube || '',
        instagram: user?.social_links?.instagram || ''
      }
    },
    mode: 'onChange'
  });

  // Update form when user data changes
  useEffect(() => {
    if (user) {
      form.reset({
        fullName: user?.display_name || user?.full_name || '',
        bio: user?.bio || '',
        username: user?.username || '',
        socialLinks: {
          tiktok: user?.social_links?.tiktok || '',
          facebook: user?.social_links?.facebook || '',
          website: user?.social_links?.website || '',
          youtube: user?.social_links?.youtube || '',
          instagram: user?.social_links?.instagram || ''
        }
      });
    }
  }, [user, form]);

  const handleAvatarUpdated = (url: string) => {
    // The avatar update is handled in the AvatarUpload component
    // We don't need to do anything here as the user metadata is updated automatically
  };

  return (
    <div className="bg-background rounded-lg shadow-xs border p-6">
      <h2 className="text-2xl font-bold text-foreground mb-6">Edit Profile</h2>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSave)} className="space-y-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="shrink-0">
              <AvatarUpload 
                currentAvatarUrl={user?.avatar_url}
                userId={user?.user_id}
                onAvatarUpdated={handleAvatarUpdated}
              />
            </div>
            
            <div className="flex-1 space-y-4">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Your display name" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="your-username" 
                        {...field}
                        onChange={(e) => {
                          // Auto-format username (lowercase, no spaces)
                          const value = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="bio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bio</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Tell us about yourself and your display"
                        className="resize-none"
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <Separator className="my-6" />

              {/* Social Media Links Section */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium">Social Media Links</h3>
                  <p className="text-sm text-muted-foreground">Add your social media profiles to help visitors connect with you</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="socialLinks.website"
                    rules={{
                      validate: (value) => {
                        const result = validateSocialMediaUrl('website', value || '');
                        return result.isValid || result.error || true;
                      }
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <Globe className="h-4 w-4" />
                          Website
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://yourwebsite.com"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="socialLinks.facebook"
                    rules={{
                      validate: (value) => {
                        const result = validateSocialMediaUrl('facebook', value || '');
                        return result.isValid || result.error || true;
                      }
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <Facebook className="h-4 w-4" />
                          Facebook
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://facebook.com/username"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="socialLinks.youtube"
                    rules={{
                      validate: (value) => {
                        const result = validateSocialMediaUrl('youtube', value || '');
                        return result.isValid || result.error || true;
                      }
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <Youtube className="h-4 w-4" />
                          YouTube
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://youtube.com/@username"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="socialLinks.instagram"
                    rules={{
                      validate: (value) => {
                        const result = validateSocialMediaUrl('instagram', value || '');
                        return result.isValid || result.error || true;
                      }
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <Instagram className="h-4 w-4" />
                          Instagram
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://instagram.com/username"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="socialLinks.tiktok"
                    rules={{
                      validate: (value) => {
                        const result = validateSocialMediaUrl('tiktok', value || '');
                        return result.isValid || result.error || true;
                      }
                    }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                          </svg>
                          TikTok
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://tiktok.com/@username"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex items-center gap-2">
                  <Save size={16} />
                  Save Changes
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={onCancel}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default UserProfileForm;
